import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const brand = params.get("brand") || "";
  const category = params.get("category") || "";
  const q = params.get("q") || "";

  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ brands: [], categories: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products/brands").then((r) => setMeta(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/products", { params: { brand: brand || undefined, category: category || undefined, q: q || undefined } })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [brand, category, q]);

  const update = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    setParams(next);
  };

  const visible = useMemo(() => products, [products]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
      <div className="overline">Catalogue</div>
      <h1 className="font-display text-5xl tracking-tight mt-2">Equipment, instruments & magnification.</h1>
      <p className="text-[var(--fm-text-2)] mt-3 max-w-2xl">Filter by brand or discipline. All listings are GST-inclusive at checkout, with white-glove pan-India delivery.</p>

      <div className="mt-10 grid lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-3 space-y-8">
          <div>
            <div className="overline mb-3">Search</div>
            <input
              type="text"
              defaultValue={q}
              data-testid="search-input"
              onKeyDown={(e) => { if (e.key === "Enter") update("q", e.currentTarget.value); }}
              placeholder="e.g. loupes, laser"
              className="w-full border border-[var(--fm-border)] bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--fm-primary)]"
            />
          </div>
          <div>
            <div className="overline mb-3">Brand</div>
            <div className="space-y-1.5">
              <button data-testid="filter-brand-all" onClick={() => update("brand", "")} className={`block text-sm ${!brand ? "text-[var(--fm-primary)] font-semibold" : "text-[var(--fm-text-2)]"}`}>All brands</button>
              {meta.brands.map((b) => (
                <button key={b} data-testid={`filter-brand-${b.toLowerCase()}`} onClick={() => update("brand", b)} className={`block text-sm ${brand === b ? "text-[var(--fm-primary)] font-semibold" : "text-[var(--fm-text-2)]"}`}>{b}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="overline mb-3">Category</div>
            <div className="space-y-1.5">
              <button onClick={() => update("category", "")} className={`block text-sm ${!category ? "text-[var(--fm-primary)] font-semibold" : "text-[var(--fm-text-2)]"}`}>All categories</button>
              {meta.categories.map((c) => (
                <button key={c} onClick={() => update("category", c)} className={`block text-sm ${category === c ? "text-[var(--fm-primary)] font-semibold" : "text-[var(--fm-text-2)]"}`}>{c}</button>
              ))}
            </div>
          </div>
        </aside>

        <section className="lg:col-span-9">
          <div className="text-xs text-[var(--fm-text-3)] mb-4" data-testid="product-count">{loading ? "Loading…" : `${visible.length} products`}</div>
          {visible.length === 0 && !loading && (
            <div className="border border-dashed border-[var(--fm-border)] p-12 text-center text-sm text-[var(--fm-text-2)]">No products match your filters.</div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
            {visible.map((p) => <ProductCard key={p.id} p={p} />)}
          </div>
        </section>
      </div>
    </div>
  );
}
